import { Typography } from "@mui/material";
import { get } from "lodash";
import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router";
import AppBar from "src/components/AppBar/AppBar";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import GuardedComponent from "src/components/GuardedComponent/GuardedComponent";
import Icon, { IconName } from "src/components/Icon/Icon";
import FilterContainer from "src/components/TableV2/FilterContainer";
import SearchField from "src/components/TableV2/SearchField";
import DisTable from "src/components/TableV2/Table";
import Title from "src/components/TableV2/Title";
import { ProspectsListType, ProspectsType } from "src/store/models/prospects.model";
import { UserRole } from "src/store/models/user.model";
import { IRootState } from "src/store/reducers";
import { fetchProspectsList, removeProspect } from "src/store/reducers/prospects";
import { hasAnyAuthority } from "src/util/roleAuthorization";
import { openUrl } from "src/util/util.navigation";
import * as S from "../../constants/StringConstants";

type IProspectsProps = StateProps & DispatchProps;
type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

const Prospects = ({ prospects, fetchProspectsList, auth, loading }: IProspectsProps) => {
  const history = useHistory();
  const [deletedId, setDeletedId] = useState<number | null>(null);

  const isAdmin = useMemo(() => {
    return hasAnyAuthority(auth.user, [UserRole.Admin]);
  }, [auth.user.userRole]);

  const columns = [
    {
      key: "firstName",
      title: S.PROSPECTS_COLUMNS.FIRST_NAME,
      style: { minWidth: "12rem", width: "20rem" },
    },
    {
      key: "lastName",
      title: S.PROSPECTS_COLUMNS.LAST_NAME,
      style: { minWidth: "12rem", width: "20rem" },
    },
    {
      key: "phone",
      title: S.PROSPECTS_COLUMNS.PHONE,
      style: { minWidth: "15rem" },
    },
    {
      key: "email",
      title: S.PROSPECTS_COLUMNS.EMAIL,
      style: { paddingRight: "5rem" },
    },
    isAdmin && {
      key: "delete",
      title: S.CO_DELETE,
      style: { minWidth: "8rem" },
      render: (item: ProspectsType) => (
        <Icon
          name={IconName.DeleteFileBlue}
          size="16px"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setDeletedId(item.id);
          }}
        />
      ),
    },
  ];
  const handleDeleteConfirm = async () => {
    await removeProspect(deletedId);
    setDeletedId(null);
  };
  const handleDeleteCancel = () => {
    setDeletedId(null);
  };
  return (
    <div className="table-view-v2__content">
      <div className="table-view__header-buttons-container">
        <AppBar icon={IconName.Prospects} title={S.PROSPECTS_PAGE_TITLE} loading={loading}>
          <div className="table-view__buttons-container">
            <GuardedComponent
              authorizedRoles={[
                UserRole.Admin,
                UserRole.ProposalSpecialist,
                UserRole.RegionalBranchManager,
                UserRole.BranchManager,
                UserRole.UnderwritingAssociate,
                UserRole.Marketer,
                UserRole.IOSpecialist,
              ]}>
              <button className="side-nav__add-button" onClick={() => openUrl("/prospects/new")}>
                <Icon name={IconName.Plus} />
                {S.NEW_PROSPECT}
              </button>
            </GuardedComponent>
          </div>
        </AppBar>
      </div>

      <DisTable
        columns={columns}
        initialState={{
          rows: 25,
          total: prospects?.total,
          map: prospects?.map || {},
          projection: prospects?.projection,
          ...prospects?.listOptions,
        }}
        onRowClick={(data: ProspectsType) => {
          history.push(`/prospects/${data.id}`);
        }}
        onUpdate={async ({ page, rows, sort, query }) => {
          const result = await fetchProspectsList({ page, rows, sort, query });
          return {
            total: get(result, "value.data.totalRows") as number,
            data: get(result, "value.data.items") as ProspectsListType[],
          };
        }}>
        <FilterContainer
          left={<Title text={S.PROSPECTS_PAGE_TITLE} />}
          right={
            <>
              <SearchField />
            </>
          }></FilterContainer>
      </DisTable>
      {deletedId && (
        <PromptModal
          open
          title={S.CO_DELETE_CONFIRM}
          confirmText={S.DOC_DELETE_YES_BUTTON}
          rejectText={S.DOC_DELETE_NO_BUTTON}
          onConfirm={handleDeleteConfirm}
          onReject={handleDeleteCancel}>
          <Typography variant="h6">{S.CO_CONFIRM_DELETE}</Typography>
        </PromptModal>
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  prospects: state.prospects,
  auth: state.auth,
  loading: state.prospects.loading,
});

const mapDispatchToProps = {
  fetchProspectsList,
  removeProspect,
};

export default connect(mapStateToProps, mapDispatchToProps)(Prospects);
